/**
 * Permission codes the Sales (order-to-cash) screens declare an interest in.
 *
 * These are the dot-strings `internal/sales/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once
 * here means a renamed permission is a compile error rather than a silently
 * invisible button.
 *
 * **The order trio is a declared segregation-of-duties triangle.**
 * `permissions.go` says it in as many words: the person who raises a sales order
 * should not be the one who confirms it against credit and stock, and neither
 * should be the one who waives the exception their own document raised. So
 * `orderConfirm`, `creditOverride` and `discountOverride` are three separately
 * permissioned decisions on the same screen, and **no action is ever inferred
 * from another**. A rep holding only `orderCreate` sees Save and nothing else —
 * not a row of disabled buttons implying they are one promotion away from
 * confirming their own discount.
 *
 * Six codes here belong to other modules and are listed because these screens
 * cannot do their job without them:
 *
 *  - `ar.customer.read` — every sales document names a customer, and the credit
 *    position on confirm is `internal/ar`'s answer (§8.4), not this module's.
 *  - `ar.invoice.read` — the INV hop of the order-to-cash chain is an AR
 *    document; without it the chain can show the shipment but not what it billed.
 *  - `inventory.item.read` / `inventory.warehouse.read` — the catalogue and the
 *    warehouses a line is priced and promised against (§8.10).
 *  - `inventory.stock.read` — **ATP**. Available-to-promise is
 *    `GET /api/v1/atp`, guarded by inventory's own read code. A user who may
 *    raise an order but not read stock sees the line grid say so plainly rather
 *    than an availability column silently reading zero, which would be a promise
 *    made from an absence of information.
 *  - `org.account.read` / `org.company.read` — the revenue account a line
 *    credits, and the company that scopes every document and report.
 */

export const SALES_PERMISSIONS = {
  /** Price lists, promotions, item margin floors — the read half. */
  pricingRead: 'sales.pricing.read',
  /** Configure them. The server guards the margin-floor write with this too. */
  pricingManage: 'sales.pricing.manage',

  quoteRead: 'sales.quote.read',
  quoteCreate: 'sales.quote.create',
  quoteUpdate: 'sales.quote.update',
  quoteDelete: 'sales.quote.delete',
  quoteSend: 'sales.quote.send',
  /** Record the customer's accept/reject. The portal (M10) uses the same service. */
  quoteDecide: 'sales.quote.decide',
  quoteConvert: 'sales.quote.convert',
  quoteCancel: 'sales.quote.cancel',

  orderRead: 'sales.order.read',
  orderCreate: 'sales.order.create',
  orderUpdate: 'sales.order.update',
  /** Runs the credit check and takes the hard stock reservation. */
  orderConfirm: 'sales.order.confirm',
  orderCancel: 'sales.order.cancel',
  /** Push an order past a failed credit check. Declared in SoD conflict with confirm. */
  creditOverride: 'sales.order.credit_override',
  /** Push an order past a discount-threshold or margin-floor refusal. */
  discountOverride: 'sales.order.discount_override',

  shipmentRead: 'sales.shipment.read',
  shipmentCreate: 'sales.shipment.create',
  shipmentReverse: 'sales.shipment.reverse',

  /** Raise the customer invoice a posted shipment earns. */
  invoiceCreate: 'sales.invoice.create',

  returnRead: 'sales.return.read',
  returnCreate: 'sales.return.create',

  subscriptionRead: 'sales.subscription.read',
  subscriptionManage: 'sales.subscription.manage',

  /** Deferred-revenue schedules. Read-only by design: the worker posts them. */
  revRecRead: 'sales.revrec.read',


  /* ---------------------------------------------- C2 commercial terms (§5.1) */

  /**
   * The discount authority matrix — who may discount what, and the margin floor
   * beneath which nothing may be sold without escalation. Separate from
   * `settingsManage` because the matrix is Sales Operations' to maintain and the
   * single global fallback threshold is not: the server guards the matrix with
   * its own pair of codes.
   */
  discountPolicyRead: 'sales.discount_policy.read',
  discountPolicyManage: 'sales.discount_policy.manage',

  /** Amend a confirmed order (design §8.3). Its own code, not `orderUpdate`. */
  orderAmend: 'sales.order.amend',

  /** Performance obligations, milestones and standalone selling prices. */
  obligationRead: 'sales.obligation.read',
  obligationManage: 'sales.obligation.manage',

  agreementRead: 'sales.agreement.read',
  agreementManage: 'sales.agreement.manage',

  rebateRead: 'sales.rebate.read',
  rebateManage: 'sales.rebate.manage',
  /**
   * Pay a rebate out. MFA-required server-side and in declared SoD conflict with
   * `rebateManage` — whoever sets the tier that computes the liability must not
   * be the one who settles it.
   */
  rebateSettle: 'sales.rebate.settle',

  contractRead: 'sales.contract.read',
  contractManage: 'sales.contract.manage',

  reportRead: 'sales.report.read',
  settingsManage: 'sales.settings.manage',

  /* ------------------------------------------------- other modules' codes */

  customerRead: 'ar.customer.read',
  arInvoiceRead: 'ar.invoice.read',

  itemRead: 'inventory.item.read',
  warehouseRead: 'inventory.warehouse.read',
  /** `GET /api/v1/atp` — available-to-promise (§8.10). */
  stockRead: 'inventory.stock.read',

  accountRead: 'org.account.read',
  companyRead: 'org.company.read',
} as const

export type SalesPermission = (typeof SALES_PERMISSIONS)[keyof typeof SALES_PERMISSIONS]
